import * as React from 'react';
import { cn } from '../lib/cn';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Initials to render when no `src` is given. Use 1–3 characters. */
  initials?: string;
  /** Optional image; if it fails to load, initials show. */
  src?: string | null;
  /** Visual size: sm (24px) / md (32px) / lg (40px). */
  size?: 'sm' | 'md' | 'lg';
}

const sizeClass = {
  sm: 'h-6 w-6 text-[10px]',
  md: 'h-8 w-8 text-xs',
  lg: 'h-10 w-10 text-sm',
} as const;

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, initials, src, size = 'md', ...props }, ref) => {
    const [failed, setFailed] = React.useState(false);
    const showImg = !!src && !failed;
    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold select-none overflow-hidden',
          sizeClass[size],
          className,
        )}
        {...props}
      >
        {showImg ? (
          <img
            src={src!}
            alt={initials ?? ''}
            className="h-full w-full object-cover"
            onError={() => setFailed(true)}
          />
        ) : (
          <span>{(initials ?? '?').slice(0, 2).toUpperCase()}</span>
        )}
      </div>
    );
  },
);
Avatar.displayName = 'Avatar';

/** Compute up-to-2-letter initials from a name like "Alex Carter" -> "AC". */
export function initialsFromName(name: string | null | undefined, fallback = '?'): string {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
